<template>
  <div>
    <fn-form-login
      :email="email"
      :enable-registration="enableRegistration"
      :errors="errors"
      :friends-light="friendsLight"
      :salon="salon"
      :full-width-inputs="fullWidthInputs"
      :preserve-value="preserveValue"
      :register-button="registerButton"
      @forgot-password="forgotPassword"
      @registration-enabled="$emit('registrationEnabled')"
      @submit="handleSubmit"
    />

    <fn-modal-ecoconsent
      v-if="ecoconsentModalOpen"
      @accept="grantEcoConsent"
      @decline="closeEcoconsentModal"
    />
  </div>
</template>

<script lang="ts">
import LocalStorage from '@/mixins/LocalStorage';
import type { PropType } from 'vue';

export default defineComponent({
  mixins: [LocalStorage],
  props: {
    redirectEnabled: {
      type: Boolean,
      default: true,
    },
    redirectTo: {
      type: String,
      default: null,
    },
    email: {
      type: String as PropType<{} | null>,
      default: null,
    },
    preserveValue: {
      type: Boolean,
    },
    registerButton: {
      type: Boolean,
    },
    enableRegistration: {
      type: Boolean,
    },
    friendsLight: {
      type: Boolean,
    },
    salon: {
      type: Boolean,
    },
    fullWidthInputs: {
      type: Boolean,
    },
  },
  emits: ['submit', 'registrationEnabled'],
  data() {
    return {
      errors: null as Record<string, any> | null,
      userLoginData: null as { email: string; password: string } | null,
      ecoconsentModalOpen: false,
    };
  },
  computed: {
    ...mapState(useMainStore, {
      isAppview: 'isAppview',
    }),

    ...mapWritableState(useMainStore, {
      isLoadingGlobal: 'isLoading',
    }),

    ...mapWritableState(useMainStore, {
      preservedEmail: 'preservedEmail',
    }),
  },
  methods: {
    ...mapActions(useMainStore, {
      loadFeatureToggles: 'loadFeatureToggles',
    }),

    ...mapActions(useUserStore, {
      login: 'login',
    }),

    closeEcoconsentModal() {
      this.ecoconsentModalOpen = false;
    },

    grantEcoConsent({ recaptchaResponse }: any) {
      this.closeEcoconsentModal();
      this.handleSubmit({
        ...this.userLoginData,
        grantEcoConsent: true,
        recaptchaResponse,
      } as any);
    },

    handleSubmit({
      email,
      password,
      grantEcoConsent,
      recaptchaResponse = null,
    }: {
      email: string;
      password: string;
      grantEcoConsent: boolean;
      recaptchaResponse: string | null;
    }) {
      this.resetErrors();
      this.isLoadingGlobal = true;
      const type = this.isAppview ? 'app' : 'basic';

      const userData: any = {
        username: email,
        password,
        type,
        recaptchaResponse,
        grantEcoConsent: !!grantEcoConsent,
      };

      this.login(userData)
        .then(async () => {
          if (this.redirectEnabled) {
            await navigateTo(this.redirectTo ? this.redirectTo : this.$t('url.account.index'));
          }

          this.deleteFromLocalStorage('failedLoginAttempts');

          this.$emit('submit');
        })
        .catch((error) => {
          this.isLoadingGlobal = false;
          const failedLoginAttempts = (this.getFromLocalStorage('failedLoginAttempts')?.value || 0) + 1;
          this.saveToLocalStorage('failedLoginAttempts', { value: failedLoginAttempts });

          if (failedLoginAttempts <= 2) {
            this.errors = {
              general: [this.$t('forms.login.errors.general')],
            };
          } else {
            this.errors = {
              general: [this.$t('forms.login.errors.restorePassword')],
            };
          }

          if (error.response?.data?.error_description === 'ecoconsent.missing') {
            this.errors.general = '';
            this.userLoginData = {
              email,
              password,
            };
            this.ecoconsentModalOpen = true;
          }
          if (error.response?.data?.error === 'access_denied') {
            this.errors.general = [this.$t('forms.login.errors.locked')];
            this.deleteFromLocalStorage('failedLoginAttempts');
          }
          if (error.response?.status === 503) {
            this.errors.general = [this.$t('forms.login.errors.unavailable')];
          }
        });
    },

    resetErrors() {
      this.errors = null;
    },

    async forgotPassword(email: string) {
      this.preservedEmail = email;
      return navigateTo(this.$t('url.password.request'));
    },
  },
});
</script>
